import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, onValue, off } from 'firebase/database';
import { rtdb } from '../firebase';
import api from '../services/api';

export default function CDPDocument() {
  const { enId } = useParams();
  const navigate = useNavigate();
  const [en, setEn] = useState(null);
  const [doc, setDoc] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [qAnswers, setQAnswers] = useState(['', '', '']);
  const [job, setJob] = useState(null); // { percent, message, stage }
  const questionsRef = useRef(null);

  useEffect(() => { loadEnrolment(); }, [enId]);

  const loadEnrolment = async () => {
    try {
      const { data } = await api.get(`/cdp/enrolments/${enId}`);
      setEn(data.enrolment);
      setDoc(data.enrolment.documentId);
      setScenario(data.enrolment.scenarioId);
    } catch { setErr('Failed to load'); } finally { setLoading(false); }
  };

  const act = async (fn) => {
    setBusy(true); setErr('');
    try { await fn(); }
    catch (e) { setErr(e.response?.data?.error || 'Something went wrong'); }
    finally { setBusy(false); }
  };

  const subscribeToJob = (jobId) => {
    const jobRef = ref(rtdb, `cdp_jobs/${jobId}`);
    setJob({ percent: 5, message: 'Starting assessment...', stage: 'starting' });
    onValue(jobRef, (snap) => {
      const val = snap.val();
      if (!val) return;
      setJob(val);
      if (val.stage === 'done') {
        off(jobRef);
        setTimeout(() => { setJob(null); loadEnrolment(); }, 800);
      }
      if (val.stage === 'error') {
        off(jobRef);
        setErr(val.message || 'Assessment failed. Please try again.');
        setBusy(false);
        setTimeout(() => setJob(null), 3000);
      }
    });
  };

  const upload = () => act(async () => {
    if (!file) throw { response: { data: { error: 'Select a .docx file' } } };
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post(`/cdp/enrolments/${enId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setEn(data.enrolment); setFile(null);

    // For main programme, auto-trigger assessment immediately
    if (!data.enrolment?.documentId?.isWarmup && !doc?.isWarmup) {
      setBusy(true);
      try {
        const { data: assessData } = await api.post(`/cdp/enrolments/${enId}/assess`, {});
        if (assessData.jobId) subscribeToJob(assessData.jobId);
      } catch (e) {
        setErr(e.response?.data?.error || 'Assessment failed');
        setBusy(false);
      }
    } else {
      // For warmups, scroll to questions section
      setTimeout(() => questionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  });

  const assess = () => act(async () => {
    const body = isWarmup ? { questionAnswers: qAnswers } : {};
    const { data } = await api.post(`/cdp/enrolments/${enId}/assess`, body);
    if (data.jobId) {
      subscribeToJob(data.jobId);
    } else {
      setEn(data.enrolment);
    }
  });

  const markResubmit = () => act(async () => {
    const { data } = await api.post(`/cdp/enrolments/${enId}/resubmit`);
    setEn(data.enrolment);
  });

  const resubmitAndUpload = () => act(async () => {
    if (!file) throw { response: { data: { error: 'Select a .docx file' } } };
    // Mark as resubmit first if still in assessed state
    if (en?.status === 'assessed') {
      await api.post(`/cdp/enrolments/${enId}/resubmit`);
    }
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post(`/cdp/enrolments/${enId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setEn(data.enrolment); setFile(null);

    // For main programme, auto-trigger assessment
    if (!doc?.isWarmup) {
      setBusy(true);
      try {
        const { data: assessData } = await api.post(`/cdp/enrolments/${enId}/assess`, {});
        if (assessData.jobId) subscribeToJob(assessData.jobId);
      } catch (e) {
        setErr(e.response?.data?.error || 'Assessment failed');
        setBusy(false);
      }
    }
  });

  const retry = () => act(async () => {
    const { data: r } = await api.post(`/cdp/enrolments/${enId}/retry`);
    const { data: s } = await api.post(`/cdp/documents/${r.documentId}/enrol`);
    navigate(`/cdp/doc/${s.enrolment._id}`, { replace: true });
  });

  if (loading) return <div style={s.center}><div style={s.spin} /></div>;
  if (err && !en) return <div style={s.center}><p style={{ color: '#b91c1c' }}>{err}</p></div>;

  const st = en?.status;
  const a = en?.assessment;
  const passed = st === 'passed';
  const daysLeft = en?.deadline ? Math.max(0, Math.ceil((new Date(en.deadline) - new Date()) / 86400000)) : null;
  const isWarmup = doc?.isWarmup;
  const briefText = isWarmup ? en?.generatedBrief : scenario?.briefText;
  const passThreshold = isWarmup ? 80 : 75;

  return (
    <div style={s.page} className="page-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <button onClick={() => navigate('/cdp')} style={s.back}>← Back to Programme</button>
        {daysLeft !== null && !passed && (
          <span style={{ fontSize: '11px', fontWeight: '600', color: daysLeft <= 2 ? '#dc2626' : '#6b7280' }}>
            {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
          </span>
        )}
      </div>
      <h1 style={s.title}>{doc?.title}</h1>
      {!isWarmup && (
        <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px' }}>
          Scenario {scenario?.code} — Attempt {en?.attemptNumber}
        </p>
      )}
      {isWarmup && (
        <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px' }}>
          {doc?.description}
        </p>
      )}
      {err && <div style={s.errBox}>{err}</div>}

      {/* Brief — always visible */}
      <div style={s.sec}>
        <h2 style={s.sh}>Your Brief Statement of Facts</h2>
        <p style={s.sd}>These facts are unique to you. Apply them to the template carefully.</p>
        <div style={s.briefBox}><pre style={s.pre}>{briefText}</pre></div>

        <p style={{ fontSize: '13px', color: '#dc2626', lineHeight: '1.6', margin: '12px 0' }}>
          Where the facts do not specify an address or other details, simply use a fictional simple ones — the assessment looks at your drafting, not the accuracy of the address.
        </p>

        {doc?.templateFileUrl && (
          <a href={doc.templateFileUrl} target="_blank" rel="noopener noreferrer" style={s.dlBtn}>
            Download Template (.docx)
          </a>
        )}

        {/* Upload section — only for first upload (enrolled) */}
        {st === 'enrolled' && (
          <div style={{ marginTop: '18px' }}>
            <h3 style={{ ...s.sh, fontSize: '16px' }}>Upload Your Completed Draft</h3>
            <p style={s.sd}>Download the template, fill every blank using your brief facts, then upload.</p>
            <div style={s.upArea}>
              <input type="file" accept=".docx,.doc" onChange={e => setFile(e.target.files[0])} style={{ fontSize: '13px' }} />
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Word document (.docx)</p>
            </div>
            <button onClick={upload} disabled={busy} style={{ ...s.btn, opacity: busy ? 0.5 : 1, marginTop: '10px' }}>
              {busy ? 'Uploading...' : isWarmup ? 'Upload Draft' : 'Upload & Submit for Assessment'}
            </button>
          </div>
        )}
      </div>

      {/* Draft uploaded — submit for assessment */}
      {st === 'draft_uploaded' && !job && (
        <div style={s.sec}>
          <div style={s.ok}>Uploaded: {en.fileName}</div>

          {/* Warm-up: prominent next-step banner */}
          {isWarmup && en.generatedQuestions?.length > 0 && (
            <div style={{
              background: '#eff6ff', border: '1.5px solid #93c5fd', borderRadius: '10px',
              padding: '14px 16px', marginBottom: '14px', display: 'flex', alignItems: 'flex-start', gap: '10px',
            }}>
              <span style={{ fontSize: '20px', flexShrink: 0 }}>&#9997;&#65039;</span>
              <div>
                <p style={{ fontSize: '13px', fontWeight: '700', color: '#1e40af', margin: '0 0 2px' }}>
                  Next Step: Answer the questions below and submit
                </p>
                <p style={{ fontSize: '12px', color: '#3b82f6', margin: 0 }}>
                  Your file is uploaded. Now answer three short questions and click "Submit for Assessment" to get your score.
                </p>
              </div>
            </div>
          )}

          <p style={s.sd}>Your brief for reference:</p>
          <div style={{ ...s.briefBox, maxHeight: '200px' }}><pre style={s.pre}>{briefText}</pre></div>

          {/* Warm-up questions */}
          {isWarmup && en.generatedQuestions?.length > 0 && (
            <div ref={questionsRef} style={{ marginTop: '16px', marginBottom: '12px' }}>
              <h3 style={{ ...s.sh, fontSize: '16px' }}>Answer These Questions</h3>
              <p style={s.sd}>Answer all three questions based on the agreement and your brief.</p>
              {en.generatedQuestions.map((q, i) => (
                <div key={i} style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#0c2340', marginBottom: '6px' }}>
                    Q{i + 1}. {q}
                  </label>
                  <textarea
                    value={qAnswers[i]}
                    onChange={e => { const a = [...qAnswers]; a[i] = e.target.value; setQAnswers(a); }}
                    placeholder="Type your answer here..."
                    rows={3}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: "'DM Sans',sans-serif", resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
            </div>
          )}

          <button
            onClick={assess}
            disabled={busy || (isWarmup && qAnswers.filter(a => a.trim()).length < 3)}
            style={{ ...s.btn, opacity: busy || (isWarmup && qAnswers.filter(a => a.trim()).length < 3) ? 0.5 : 1, marginTop: '10px' }}
          >
            {busy ? 'Assessing (may take a minute)...' : 'Submit for Assessment'}
          </button>
        </div>
      )}

      {(st === 'assessing' || job) && (
        <div style={{ ...s.sec, textAlign: 'center' }}>
          <div style={s.spin} />
          <p style={{ color: '#0c2340', fontWeight: '700', fontSize: '14px', marginTop: '12px' }}>
            {job?.message || 'Your draft is being assessed...'}
          </p>
          {job && (
            <div style={{ margin: '16px auto 0', maxWidth: '320px' }}>
              <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '99px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${job.percent || 0}%`,
                  background: 'linear-gradient(90deg, #0c2340, #1e40af)',
                  borderRadius: '99px',
                  transition: 'width 0.6s ease',
                }} />
              </div>
              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>{job.percent || 0}% complete</p>
            </div>
          )}
          <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px' }}>This usually takes 30–60 seconds. Please don't close this page.</p>
        </div>
      )}

      {/* Assessment results */}
      {a && ['assessed', 'passed', 'resubmit'].includes(st) && (
        <div style={s.sec}>
          <h2 style={s.sh}>Assessment Results</h2>

          {/* Score display */}
          <div style={{ ...s.scoreBox, background: passed ? '#f0fdf4' : '#fef2f2', borderColor: passed ? '#bbf7d0' : '#fecaca' }}>
            <span style={{ fontSize: '42px', fontWeight: '800', color: passed ? '#065f46' : '#991b1b' }}>
              {a.totalScore}
            </span>
            <span style={{ fontSize: '18px', color: '#9ca3af' }}> / 100</span>
            <p style={{ fontSize: '14px', fontWeight: '700', color: passed ? '#065f46' : '#991b1b', marginTop: '4px' }}>
              {passed ? 'PASS — Well done!' : `Below ${passThreshold} — Please correct and resubmit.`}
            </p>
          </div>

          {/* Warm-up Part I — Three bullet points */}
          {isWarmup && en.warmupPartI && (
            <div style={s.part}>
              <h3 style={s.ph}>Part 1 — Feedback</h3>
              <ul style={{ fontSize: '13px', color: '#1e293b', lineHeight: '1.7', margin: 0, paddingLeft: '18px' }}>
                {en.warmupPartI.correct && <li style={{ marginBottom: '6px' }}><strong>What you did well:</strong> {en.warmupPartI.correct}</li>}
                {en.warmupPartI.mainError && <li style={{ marginBottom: '6px' }}><strong>Main area to fix:</strong> {en.warmupPartI.mainError}</li>}
                {en.warmupPartI.encouragement && <li><strong>Note:</strong> {en.warmupPartI.encouragement}</li>}
              </ul>
            </div>
          )}

          {/* Warm-up Part II — Question results */}
          {isWarmup && en.questionResults?.length > 0 && (
            <div style={s.part}>
              <h3 style={s.ph}>Part 2 — Questions</h3>
              {en.questionResults.map((qr, i) => (
                <div key={i} style={{ marginBottom: i < en.questionResults.length - 1 ? '14px' : 0, paddingBottom: i < en.questionResults.length - 1 ? '14px' : 0, borderBottom: i < en.questionResults.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                  <p style={{ fontSize: '13px', fontWeight: '700', color: '#0c2340', margin: '0 0 4px' }}>Q{i + 1}. {qr.question}</p>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px' }}>Your answer: {qr.answer || '(empty)'}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '6px',
                      background: qr.result === 'Correct' ? '#d1fae5' : qr.result === 'Partially Correct' ? '#fef3c7' : '#fef2f2',
                      color: qr.result === 'Correct' ? '#065f46' : qr.result === 'Partially Correct' ? '#92400e' : '#991b1b',
                    }}>{qr.result}</span>
                    <span style={{ fontSize: '12px', color: '#374151' }}>{qr.feedback}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Main programme Part 1 — Overall Observation */}
          {!isWarmup && (
            <div style={s.part}>
              <h3 style={s.ph}>Part 1 — Overall Observation</h3>
              <p style={{ fontSize: '13px', color: '#1e293b', lineHeight: '1.7', margin: 0 }}>{a.overallObservation}</p>
            </div>
          )}

          {/* Corrections / Errors table */}
          {a.errorList?.length > 0 && (
            <div style={s.part}>
              <h3 style={s.ph}>{isWarmup ? 'Corrections' : 'Part 2 — Factual Inconsistencies and Errors'}</h3>
              {isWarmup && en.failedAttempts >= 3 && (
                <p style={{ fontSize: '12px', color: '#92400e', background: '#fef3c7', padding: '8px 12px', borderRadius: '8px', marginBottom: '10px' }}>
                  Here is exactly what each blank should contain. Update your document with these corrections and resubmit.
                </p>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th style={s.th}>#</th>
                      <th style={s.th}>{isWarmup ? 'Blank' : 'Clause / Section'}</th>
                      <th style={s.th}>{isWarmup ? 'What You Filled' : 'Error / Issue'}</th>
                      <th style={s.th}>{isWarmup ? 'Correct Answer' : 'Correction Required'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.errorList.map((e, i) => (
                      <tr key={i}>
                        <td style={s.td}>{i + 1}</td>
                        <td style={s.td}>{e.clauseSection}</td>
                        <td style={s.td}>{e.errorIssue}</td>
                        <td style={s.td}>{e.correctionRequired}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Main programme scoring breakdown */}
          {!isWarmup && (
            <div style={s.part}>
              <h3 style={s.ph}>Scoring Breakdown</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th style={s.th}>Assessment Area</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Max</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Awarded</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'preamblePartyDetails', label: 'Preamble and Party Details correctly filled' },
                    { key: 'recitals', label: 'Recitals correctly reflecting the facts' },
                    { key: 'keyCommercialTerms', label: 'Key commercial terms correctly applied' },
                    { key: 'definedTerms', label: 'Defined terms correctly used throughout' },
                    { key: 'blanksSchedules', label: 'Blanks and schedules completed' },
                    { key: 'internalConsistency', label: 'Internal consistency of the document' },
                    { key: 'overallReasonableness', label: 'Overall commercial reasonableness' },
                  ].map(row => (
                    <tr key={row.key}>
                      <td style={s.td}>{row.label}</td>
                      <td style={{ ...s.td, textAlign: 'center', fontWeight: '600' }}>{a.scoring?.[row.key]?.max ?? '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center', fontWeight: '700', color: (a.scoring?.[row.key]?.awarded ?? 0) >= (a.scoring?.[row.key]?.max ?? 0) * 0.7 ? '#065f46' : '#991b1b' }}>
                        {a.scoring?.[row.key]?.awarded ?? '—'}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ ...s.td, fontWeight: '800' }}>TOTAL</td>
                    <td style={{ ...s.td, textAlign: 'center', fontWeight: '800' }}>100</td>
                    <td style={{ ...s.td, textAlign: 'center', fontWeight: '800', color: a.totalScore >= 75 ? '#065f46' : '#991b1b' }}>{a.totalScore}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Assessor comments */}
          {a.assessorComments && (
            <div style={s.part}>
              <h3 style={s.ph}>Assessor Comments</h3>
              <p style={{ fontSize: '13px', color: '#1e293b', lineHeight: '1.7', margin: 0 }}>{a.assessorComments}</p>
            </div>
          )}

          {/* Actions */}
          {passed && (
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button onClick={() => navigate('/cdp')} style={{ ...s.btn, background: '#065f46' }}>Back to Programme</button>
            </div>
          )}
          {(st === 'assessed' || st === 'resubmit') && a.result === 'resubmit' && (
            <div style={{ marginTop: '16px' }}>
              <h3 style={s.ph}>Reupload Corrected Draft</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px', lineHeight: '1.6' }}>
                Review the feedback above, correct your draft, and reupload. No penalty. No judgment.
              </p>
              <div style={s.upArea}>
                <input type="file" accept=".docx,.doc" onChange={e => setFile(e.target.files[0])} style={{ fontSize: '13px' }} />
                <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Word document (.docx)</p>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                <button onClick={resubmitAndUpload} disabled={busy || !file} style={{ ...s.btn, opacity: busy || !file ? 0.5 : 1 }}>
                  {busy ? 'Uploading...' : isWarmup ? 'Upload Corrected Draft' : 'Upload & Reassess'}
                </button>
                {!isWarmup && (
                  <button onClick={retry} disabled={busy} style={{ ...s.btn, background: '#6b7280', opacity: busy ? 0.5 : 1 }}>
                    {busy ? 'Starting...' : 'Start Fresh (New Scenario)'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  page: { maxWidth: '800px', margin: '0 auto', padding: '28px 20px 60px' },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 60px)' },
  spin: { width: '28px', height: '28px', borderRadius: '50%', border: '2.5px solid #e5e7eb', borderTop: '2.5px solid #0c2340', animation: 'spin 0.8s linear infinite', margin: '0 auto' },
  back: { background: 'none', border: 'none', color: '#0c2340', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: 0 },
  title: { fontFamily: "'DM Sans',sans-serif", fontSize: '24px', fontWeight: '800', color: '#0c2340', margin: 0 },
  errBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '10px 14px', fontSize: '13px', color: '#991b1b', marginBottom: '14px' },
  ok: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '10px 14px', fontSize: '13px', color: '#065f46', marginBottom: '10px' },
  sec: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '22px', marginBottom: '16px' },
  sh: { fontSize: '17px', fontWeight: '700', color: '#0c2340', margin: '0 0 4px' },
  sd: { fontSize: '13px', color: '#6b7280', marginBottom: '12px', lineHeight: '1.6' },
  briefBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px', maxHeight: '400px', overflow: 'auto', marginBottom: '12px' },
  pre: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '13px', lineHeight: '1.7', color: '#1e293b', margin: 0, fontFamily: "'DM Sans',sans-serif" },
  dlBtn: { display: 'inline-block', background: '#0c2340', color: '#fff', padding: '10px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', textDecoration: 'none' },
  upArea: { background: '#f8fafc', border: '2px dashed #d1d5db', borderRadius: '12px', padding: '18px', textAlign: 'center' },
  btn: { background: '#0c2340', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" },
  part: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', marginBottom: '14px', marginTop: '14px' },
  ph: { fontSize: '14px', fontWeight: '700', color: '#0c2340', margin: '0 0 8px' },
  scoreBox: { border: '1px solid', borderRadius: '14px', padding: '24px', textAlign: 'center', marginBottom: '14px' },
  th: { padding: '8px 10px', borderBottom: '2px solid #e2e8f0', textAlign: 'left', fontWeight: '700', color: '#0c2340', fontSize: '12px' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#374151', lineHeight: '1.5' },
};
